import React, { useState, useEffect, useRef } from 'react';
// A 128px WebP bundled with the app. The 1024px PNG this used to pull from
// Storage was 1.4 MB on every page load — about 100 MB of cached egress a
// day before a single game was played (2026-09-23).
import tastedLogo from '../../assets/tasted-logo.webp';

const __DEV__ = import.meta.env.DEV;

export default function LoadingScreen({ loading, message }) {
  const instanceIdRef = useRef(Math.random().toString(36).slice(2));
  // Keep the overlay mounted to prevent content flash before it appears
  const [shouldRender] = useState(true);
  const [visible, setVisible] = useState(false);
  const startTimeRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const unmountTimeoutRef = useRef(null);
  const fadeInLogTimeoutRef = useRef(null);
  const FADE_IN_MS = 150; // faster fade-in to avoid content pop-in
  const FADE_OUT_MS = 250; // smoother fade-out
  const MIN_VISIBLE_MS = 250; // minimum time to keep the loader shown before starting fade-out
  // Last-resort backstop. The overlay is `fixed inset-0 z-50` and blocks input
  // while visible, and `visible` only clears when the `loading` prop flips back
  // to false. If that hide signal never arrives (e.g. a public-loader-hide
  // gated on a Supabase call that stalls under load), the overlay would trap the
  // user indefinitely — the exact avatar→lobby freeze seen in the 3-player E2E.
  // This forces the overlay down regardless of the prop; it sits ABOVE every
  // designed safety (App.jsx's 5s game / 8s public timers) so it only ever fires
  // when those failed. The screen underneath is already rendered.
  const MAX_VISIBLE_MS = 9000;
  const [fadeDurationMs, setFadeDurationMs] = useState(FADE_OUT_MS);

  // Mount/unmount logging (dev only)
  useEffect(() => {
    if (!__DEV__) return;
    const now = Date.now();
    const id = instanceIdRef.current;
    console.log('[LS] MOUNT', {
      id,
      time: now,
      iso: new Date(now).toISOString(),
      initialLoadingProp: loading,
      path:
        typeof window !== 'undefined'
          ? window.location.pathname + window.location.search
          : undefined,
    });
    return () => {
      const t = Date.now();
      console.log('[LS] COMPONENT_UNMOUNT', {
        id,
        time: t,
        iso: new Date(t).toISOString(),
      });
    };
  }, [loading]);

  // Prop change logging (dev only)
  const prevLoadingRef = useRef(loading);
  useEffect(() => {
    if (prevLoadingRef.current !== loading) {
      const t = Date.now();
      if (__DEV__) console.log('[LS] PROP_CHANGE', {
        id: instanceIdRef.current,
        prev: prevLoadingRef.current,
        next: loading,
        time: t,
        iso: new Date(t).toISOString(),
      });
      prevLoadingRef.current = loading;
    }
  }, [loading]);

  useEffect(() => {
    // Clear any pending timers when loading state changes
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (unmountTimeoutRef.current) {
      clearTimeout(unmountTimeoutRef.current);
      unmountTimeoutRef.current = null;
    }

    if (loading) {
      // Start fade-in immediately to avoid any frame where content shows without overlay
      const t = Date.now();
      if (__DEV__) console.log('[LS] FADE_IN_START', {
        id: instanceIdRef.current,
        t,
        iso: new Date(t).toISOString(),
      });
      setFadeDurationMs(FADE_IN_MS);
      setVisible(true);
      // Log fade-in complete at ~1.25s (dev only)
      if (fadeInLogTimeoutRef.current)
        clearTimeout(fadeInLogTimeoutRef.current);
      fadeInLogTimeoutRef.current = setTimeout(() => {
        const done = Date.now();
        if (__DEV__) console.log('[LS] FADE_IN_COMPLETE', {
          id: instanceIdRef.current,
          t: done,
          iso: new Date(done).toISOString(),
          elapsedMs: done - t,
        });
      }, FADE_IN_MS);
      startTimeRef.current = Date.now();
    } else if (startTimeRef.current !== null) {
      // Enforce minimum on-screen time before starting fade-out
      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);

      const beginFadeOut = () => {
        const t = Date.now();
        if (__DEV__) console.log('[LS] FADE_OUT_START', {
          id: instanceIdRef.current,
          t,
          iso: new Date(t).toISOString(),
          delayedMs: remaining,
        });
        setFadeDurationMs(FADE_OUT_MS);
        setVisible(false);
        unmountTimeoutRef.current = setTimeout(() => {
          const u = Date.now();
          if (__DEV__) console.log('[LS] UNMOUNT', {
            id: instanceIdRef.current,
            t: u,
            iso: new Date(u).toISOString(),
          });
          try {
            if (typeof window !== 'undefined') {
              window.__appLoaderHiddenAt = u;
              const evt = new CustomEvent('app-loader-hidden', {
                detail: { time: u, iso: new Date(u).toISOString() },
              });
              window.dispatchEvent(evt);
              if (__DEV__) console.log('[LS] APP_LOADER_HIDDEN_EVENT', {
                id: instanceIdRef.current,
                time: u,
                iso: new Date(u).toISOString(),
              });
            }
          } catch (err) {
            if (__DEV__) console.warn(
              '[LS] Failed to dispatch app-loader-hidden event',
              err
            );
          }
          // Keep mounted to ensure immediate availability for the next show
          startTimeRef.current = null;
        }, FADE_OUT_MS);
      };

      if (remaining > 0) {
        if (__DEV__) console.log('[LS] MIN_VISIBLE_DELAY', {
          id: instanceIdRef.current,
          remaining,
          elapsed,
        });
        hideTimeoutRef.current = setTimeout(beginFadeOut, remaining);
      } else {
        beginFadeOut();
      }
    }

    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      if (unmountTimeoutRef.current) clearTimeout(unmountTimeoutRef.current);
      if (fadeInLogTimeoutRef.current)
        clearTimeout(fadeInLogTimeoutRef.current);
    };
  }, [loading]);

  // Self-healing watchdog: whenever the overlay is visible, arm a hard timer that
  // force-hides it if it overstays MAX_VISIBLE_MS. Guarantees a stuck loader can
  // never trap the user, independent of the `loading` prop / event bus.
  useEffect(() => {
    if (!visible) return undefined;
    const watchdogId = setTimeout(() => {
      // Unconditional warn: a fired watchdog means a hide signal was lost — a
      // real anomaly worth a prod breadcrumb, not just a dev log.
      // eslint-disable-next-line no-console
      console.warn('[LS] max-visible watchdog fired — force-hiding stuck loader');
      setVisible(false);
      startTimeRef.current = null;
      try {
        if (typeof window !== 'undefined') {
          window.__appLoaderHiddenAt = Date.now();
          window.dispatchEvent(
            new CustomEvent('app-loader-hidden', { detail: { watchdog: true } })
          );
        }
      } catch {
        /* ignore — force-hide already applied */
      }
    }, MAX_VISIBLE_MS);
    return () => clearTimeout(watchdogId);
  }, [visible]);

  if (!shouldRender) return null;

  return (
    <div
      className={
        `fixed inset-0 z-[10000] flex flex-col items-center justify-center min-h-[100dvh] h-[100dvh] bg-wine-background backdrop-blur-sm transition-opacity ease-out ` +
        (visible ? 'opacity-100 ' : 'opacity-0 ') +
        // Stop intercepting clicks the moment `loading` goes false — even while
        // the fade-out is still animating — so a fading overlay never blocks the
        // interactive screen beneath it.
        (visible && loading ? '' : 'pointer-events-none')
      }
      // Faded out, the overlay stays mounted over every screen; without this a
      // screen reader keeps finding "Tasted, Loading..." after the page loaded.
      aria-hidden={visible ? undefined : 'true'}
      style={{
        willChange: 'opacity',
        transitionDuration: `${fadeDurationMs}ms`,
      }}
    >
      <div className="flex flex-col items-center space-y-4">
        {/* Ring loader with centered logo (dark green). The spin is essential
            feedback; under reduced motion it becomes a slow fade. */}
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 border-8 border-wine-primary/30 rounded-full"></div>
          <div className="absolute inset-0 border-8 border-wine-primary border-t-transparent rounded-full animate-spin motion-reduce:animate-pulse"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <img
              src={tastedLogo}
              alt="Tasted"
              className="w-8 h-8"
              decoding="async"
              loading="eager"
              referrerPolicy="no-referrer"
              onError={(e) => {
                // Fallback to local asset if remote fetch fails
                e.currentTarget.src = '/tasted.ico';
              }}
            />
          </div>
        </div>

        {/* Loading text */}
        <p className="text-lg text-wine-text-heading font-medium">{message || 'Loading...'}</p>
      </div>
    </div>
  );
}
